import React from 'react';
import Icon from '../../../components/AppIcon';

const TimelineSection = () => {
  const milestones = [
    {
      id: 1,
      year: "2010",
      title: "Foundation",
      description: "REXFEL founded in Hamburg with €50M initial fund, focusing on European tech startups.",
      icon: "Rocket",
      achievements: ["First fund established", "Hamburg office opened", "Initial team of 5 professionals"]
    },
    {
      id: 2,
      year: "2013",
      title: "First Major Exit",
      description: "Successful exit of portfolio company Mobellex, generating 15x return for investors.",
      icon: "TrendingUp",
      achievements: ["€150M exit value", "15x return multiple", "Industry recognition"]
    },
    {
      id: 3,
      year: "2016",
      title: "Fund II Launch",
      description: "Raised €200M second fund, expanding focus to include fintech and digital transformation.",
      icon: "Target",
      achievements: ["€200M fund raised", "Fintech expertise added", "Team expanded to 15"]
    },
    {
      id: 4,
      year: "2019",
      title: "Digital Innovation",
      description: "Launched digital investment platform and began crypto market analysis capabilities.",
      icon: "Cpu",
      achievements: ["Digital platform launched", "Crypto research team", "AI-powered due diligence"]
    },
    {
      id: 5,
      year: "2021",
      title: "International Expansion",
      description: "Opened satellite offices and established partnerships across Europe and North America.",
      icon: "Globe",
      achievements: ["Multi-country presence", "Strategic partnerships", "€500M AUM milestone"]
    },
    {
      id: 6,
      year: "2023",
      title: "Fund III Success",
      description: "Closed €750M third fund, becoming one of Europe's leading strategic investment firms.",
      icon: "Award",
      achievements: ["€750M fund closed", "50+ portfolio companies", "Industry leadership position"]
    },
    {
      id: 7,
      year: "2024",
      title: "Future Vision",
      description: "Launching innovation lab and expanding into Web3, AI, and sustainable technology investments.",
      icon: "Lightbulb",
      achievements: ["Innovation lab opened", "Web3 investments", "Sustainability focus"]
    }
  ];

  return (
    <section className="py-20 bg-muted/30">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center max-w-4xl mx-auto mb-16">
          <div className="flex items-center justify-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-brand bg-gradient-to-br from-primary to-accent flex items-center justify-center">
              <Icon name="Clock" size={20} className="text-white" />
            </div>
            <span className="text-primary font-semibold text-lg">Our Journey</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-foreground mb-6">
            15 Years of
            <span className="text-gradient-brand block">Strategic Growth</span>
          </h2>
          
          <p className="text-xl text-muted-foreground leading-relaxed">
            From a Hamburg-based startup to a leading European investment firm, discover the key 
            milestones that shaped REXFEL's journey and strategic evolution.
          </p>
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary via-accent to-primary transform md:-translate-x-0.5"></div>

          {/* Timeline Items */}
          <div className="space-y-12">
            {milestones?.map((milestone, index) => (
              <div 
                key={milestone?.id}
                className={`relative flex items-center ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                } flex-col md:flex-row`}
              >
                {/* Timeline Node */}
                <div className="absolute left-8 md:left-1/2 w-4 h-4 bg-gradient-to-br from-primary to-accent rounded-full transform md:-translate-x-2 z-10 shadow-brand">
                  <div className="absolute inset-0.5 bg-background rounded-full"></div>
                  <div className="absolute inset-1 bg-gradient-to-br from-primary to-accent rounded-full"></div>
                </div>

                {/* Content Card */}
                <div className={`w-full md:w-5/12 ml-16 md:ml-0 ${
                  index % 2 === 0 ? 'md:mr-auto md:pr-8' : 'md:ml-auto md:pl-8'
                }`}>
                  <div className={`bg-card rounded-brand-lg p-6 shadow-brand hover-lift transition-brand border border-border scroll-reveal stagger-${(index % 3) + 1}`}>
                    <div className="flex items-center space-x-4 mb-4">
                      <div className="w-12 h-12 rounded-brand bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                        <Icon name={milestone?.icon} size={20} className="text-white" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-primary">{milestone?.year}</div>
                        <h3 className="text-xl font-bold text-foreground">{milestone?.title}</h3>
                      </div>
                    </div>
                    
                    <p className="text-muted-foreground leading-relaxed mb-4">
                      {milestone?.description}
                    </p>
                    
                    <div className="space-y-2">
                      {milestone?.achievements?.map((achievement, achievementIndex) => (
                        <div key={achievementIndex} className="flex items-center space-x-2">
                          <Icon name="CheckCircle" size={14} className="text-success flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{achievement}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Year Badge (Mobile) */}
                <div className="md:hidden absolute left-0 top-0 bg-primary text-primary-foreground text-xs font-bold px-2 py-1 rounded-brand-xs">
                  {milestone?.year}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="mt-20 bg-gradient-to-br from-primary/5 to-accent/5 rounded-brand-lg p-8 lg:p-12 border border-primary/10">
          <div className="text-center mb-8">
            <h3 className="text-2xl lg:text-3xl font-bold text-foreground mb-4">
              Our Impact Over 15 Years
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Consistent growth and strategic evolution have positioned REXFEL as a trusted 
              partner for entrepreneurs and a leader in European venture capital.
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-primary counter-animate">€2.5B</div>
              <div className="text-sm text-muted-foreground font-medium">Total Assets Under Management</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-primary counter-animate">50+</div>
              <div className="text-sm text-muted-foreground font-medium">Portfolio Companies</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-primary counter-animate">25+</div>
              <div className="text-sm text-muted-foreground font-medium">Successful Exits</div>
            </div>
            <div>
              <div className="text-3xl lg:text-4xl font-bold text-primary counter-animate">12x</div>
              <div className="text-sm text-muted-foreground font-medium">Average Return Multiple</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;